import { ButtonHTMLAttributes, ComponentClass, createElement, forwardRef, FunctionComponent, NamedExoticComponent, ReactNode } from "react";
import classNames from "classnames";
import { RefType } from "../../types";
import { genericMemo } from "../../utils";

import styles from "./styles.module.scss";

export type ButtonSize = "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
export type ButtonType =
  "primary"
  | "secondaryGray"
  | "secondaryColor"
  | "tertiaryGray"
  | "tertiaryColor"
  | "linkGray"
  | "linkColor"
  | "destructivePrimary"
  | "destructiveSecondary"
  | "destructiveTertiary"
  | "destructiveLink"
  | "successPrimary"
  | "successSecondary"
  | "successTertiary"
  | "successLink";

export type BaseButtonProps<P extends object = ButtonHTMLAttributes<HTMLButtonElement>, R extends unknown = HTMLButtonElement> = {
  component?: string | FunctionComponent<P> | ComponentClass<P, any> | NamedExoticComponent<P>,
  ref?: RefType<R>,
  className?: string,
  buttonSize?: ButtonSize,
  buttonType?: ButtonType;
  disabled?: boolean;
  children?: ReactNode,
  iconOnly?: boolean
}

export type ButtonProps<P extends object = ButtonHTMLAttributes<HTMLButtonElement>, R extends unknown = HTMLButtonElement> =
  BaseButtonProps<P, R> & P;

const ButtonComponent = <P extends object = ButtonHTMLAttributes<HTMLButtonElement>, R extends unknown = HTMLButtonElement>(props: ButtonProps<P, R>) => <></>;
type ButtonComponentType = typeof ButtonComponent;

export const Button = genericMemo(forwardRef(<P extends object = ButtonHTMLAttributes<HTMLButtonElement>, R extends unknown = HTMLButtonElement>(
  {
    component = "button",
    disabled = false,
    buttonSize = "md",
    buttonType = "primary",
    className,
    children,
    iconOnly,
    ...props
  }: ButtonProps<P, R>,
  ref: RefType<R>,
) => {
  return createElement(component as any, {
    ref,
    disabled,
    className: classNames(
      className,
      styles.root,
      styles[buttonSize as any],
      styles[buttonType as any],
      {
        [styles.disabled]: disabled,
        [styles.iconOnly]: iconOnly,
      },
    ),
    children: children,
    ...props,
  });
}) as ButtonComponentType);
